import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Send, Copy, Pencil, Eye, Loader2, CheckCircle2, XCircle, Clock, Zap, User, MailOpen, MousePointerClick, Target, Activity, TestTube2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const badge = (status) => ({
  draft: "bg-slate-100 text-slate-700",
  scheduled: "bg-amber-100 text-amber-700",
  sending: "bg-blue-100 text-[#0044FF]",
  sent: "bg-emerald-100 text-emerald-700",
}[status] || "bg-slate-100");

const triggerBadge = (t) => ({
  manual: { icon: User, label: "Manual", cls: "bg-blue-50 text-[#0044FF]" },
  scheduled: { icon: Clock, label: "Scheduled", cls: "bg-amber-50 text-amber-700" },
  automation: { icon: Zap, label: "Automation", cls: "bg-purple-50 text-purple-700" },
}[t] || { icon: User, label: t, cls: "bg-slate-50" });

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");
  const [tracking, setTracking] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testSending, setTestSending] = useState(false);

  const sendTestEmail = async () => {
    if (!testEmailAddr || !testEmailAddr.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!c.body || !c.body.trim()) { toast.error("This campaign has no body content to send"); return; }
    setTestSending(true);
    try {
      const { data } = await api.post("/email/test-send", { subject: c.subject, body: c.body, attachments: c.attachments, to_email: testEmailAddr });
      toast.success(`Test sent via ${data.provider} from ${data.sent_from}`);
      setTestEmailOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setTestSending(false); }
  };

  const load = () => api.get(`/campaigns/${id}`).then(({ data }) => setC(data));

  const loadTracking = async () => {
    setTrackLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${id}/tracking`);
      setTracking(data);
    } catch (e) { /* ignore */ }
    finally { setTrackLoading(false); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [id]);
  useEffect(() => { if (tab === "tracking") loadTracking(); }, [tab]);

  if (!c) return <div className="p-10 text-slate-400">Loading…</div>;

  const total = (c.sent_count || 0) + (c.failed_count || 0);
  const rate = total ? Math.round(((c.sent_count || 0) * 100) / total) : 0;
  const trig = triggerBadge(c.trigger_type || "manual");
  const TIcon = trig.icon;

  const resend = async () => {
    if (!window.confirm("Send this campaign again to the same audience?")) return;
    try {
      await api.post(`/campaigns/${id}/send`);
      toast.success("Re-send started");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/campaigns/${id}/duplicate`);
      toast.success("Duplicated as draft");
      nav(`/campaigns/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="campaign-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/campaigns")} className="gap-1" data-testid="detail-back"><ArrowLeft className="w-4 h-4" /> Campaigns</Button>
        <span style={{ color: "var(--border)" }}>/</span>
        <Badge className={badge(c.status)}>{c.status}</Badge>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${trig.cls}`}>
          <TIcon className="w-3 h-3" /> {trig.label}
        </span>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="display text-4xl truncate" style={{ color: "var(--text)" }} title={c.name}>{c.name}</h1>
          <p className="mt-2 truncate" style={{ color: "var(--text-soft)" }}>{c.subject}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {testEmailOpen ? (
            <div className="flex items-center gap-2">
              <Input data-testid="detail-test-email-input" value={testEmailAddr} onChange={(e) => setTestEmailAddr(e.target.value)} placeholder="you@email.com" className="h-9 w-48" />
              <Button size="sm" data-testid="detail-test-email-send" onClick={sendTestEmail} disabled={testSending} className="h-9 text-white gap-1" style={{ background: "var(--brand)" }}>
                {testSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
              </Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => setTestEmailOpen(false)}><X className="w-3.5 h-3.5" /></Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setTestEmailOpen(true)} data-testid="detail-test-email-btn" className="gap-2"><TestTube2 className="w-4 h-4" /> Send Test</Button>
          )}
          <Button variant="outline" onClick={duplicate} data-testid="btn-duplicate" className="gap-2"><Copy className="w-4 h-4" /> Duplicate</Button>
          <Button variant="outline" onClick={() => nav(`/campaigns?edit=${id}`)} data-testid="btn-edit" className="gap-2"><Pencil className="w-4 h-4" /> Edit</Button>
          {c.status !== "sending" && (
            <Button onClick={resend} data-testid="btn-resend" className="gap-2 text-white" style={{ background: "var(--brand)" }}>
              <Send className="w-4 h-4" /> {c.status === "sent" ? "Send Again" : "Send"}
            </Button>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold" style={{ color: "var(--text-soft)" }}>Sent</div>
          <div className="display text-3xl mt-2 text-emerald-600">{c.sent_count || 0}</div>
        </Card>
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold" style={{ color: "var(--text-soft)" }}>Failed</div>
          <div className="display text-3xl mt-2" style={{ color: "var(--brand-accent, #FF4D00)" }}>{c.failed_count || 0}</div>
        </Card>
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold" style={{ color: "var(--text-soft)" }}>Delivery rate</div>
          <div className="display text-3xl mt-2" style={{ color: "var(--text)" }}>{rate}%</div>
        </Card>
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold flex items-center gap-1" style={{ color: "var(--text-soft)" }}><MailOpen className="w-3 h-3" /> Open rate</div>
          <div className="display text-3xl mt-2" style={{ color: c.open_rate ? "var(--brand)" : "var(--text-soft)" }}>
            {c.open_rate != null ? `${c.open_rate}%` : "—"}
          </div>
          {c.tracking_total ? <div className="text-xs mt-1 mono" style={{ color: "var(--text-soft)" }}>{c.open_count || 0} / {c.tracking_total} tracked</div> : null}
        </Card>
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold flex items-center gap-1" style={{ color: "var(--text-soft)" }}><MousePointerClick className="w-3 h-3" /> Click rate</div>
          <div className="display text-3xl mt-2" style={{ color: c.click_rate ? "var(--brand)" : "var(--text-soft)" }}>
            {c.click_rate != null ? `${c.click_rate}%` : "—"}
          </div>
          {c.tracking_total ? <div className="text-xs mt-1 mono" style={{ color: "var(--text-soft)" }}>{c.click_count || 0} / {c.tracking_total} tracked</div> : null}
        </Card>
        <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="text-xs uppercase tracking-wider mono font-semibold" style={{ color: "var(--text-soft)" }}>Total recipients</div>
          <div className="display text-3xl mt-2" style={{ color: "var(--text)" }}>{total}</div>
        </Card>
      </div>

      {/* Meta grid */}
      <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Sent from</div><div className="font-medium">{c.sent_from || "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Sent by</div><div className="font-medium">{c.sent_by_email || "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Sent at</div><div className="font-medium mono">{c.sent_at ? new Date(c.sent_at).toLocaleString() : "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Audience</div><div className="font-medium">{c.audience_tag ? <Badge variant="outline" style={{ borderColor: "var(--brand)", color: "var(--brand)" }}>{c.audience_tag.startsWith("__email:") ? `Single recipient: ${c.audience_tag.slice(8)}` : c.audience_tag}</Badge> : "All contacts"}</div></div>
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Provider breakdown</div><div className="font-medium mono text-xs">{c.provider_counts ? Object.entries(c.provider_counts).filter(([,v]) => v > 0).map(([k,v]) => `${k}: ${v}`).join(" · ") || "—" : "—"}</div></div>
          <div><div className="text-xs uppercase tracking-wider mono font-semibold mb-1" style={{ color: "var(--text-soft)" }}>Attachments</div><div className="font-medium text-xs">{(c.attachments || []).length ? c.attachments.map(a => a.name).join(", ") : "None"}</div></div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-preview"><Eye className="w-4 h-4 mr-1" /> Preview</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="tab-deliveries">Deliveries ({(c.deliveries || []).length})</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking"><Activity className="w-4 h-4 mr-1" /> Tracking</TabsTrigger>
          <TabsTrigger value="html" data-testid="tab-html">HTML</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card style={{ background: "var(--surface)", borderColor: "var(--border)" }} className="overflow-hidden">
            <div className="px-6 py-3 border-b text-sm" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <div style={{ color: "var(--text-soft)" }}>From: <span className="font-medium" style={{ color: "var(--text)" }}>{c.sent_from || "—"}</span></div>
              <div style={{ color: "var(--text-soft)" }} className="mt-1">Subject: <span className="font-medium" style={{ color: "var(--text)" }}>{c.subject}</span></div>
            </div>
            <div className="p-6 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body || "<em>Empty body</em>") }} />
          </Card>
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <Card style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <Table>
              <TableHeader><TableRow style={{ background: "var(--bg)" }}>
                <TableHead>Recipient</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Reason (if failed)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(c.deliveries || []).map((d, i) => (
                  <TableRow key={i} data-testid={`delivery-${i}`}>
                    <TableCell className="font-semibold">{d.name || "—"}</TableCell>
                    <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{d.email}</TableCell>
                    <TableCell>
                      {d.status === "failed" ? (
                        <Badge className="bg-red-100 text-red-700 gap-1"><XCircle className="w-3 h-3" /> Failed</Badge>
                      ) : d.delivery_status === "delivered" ? (
                        <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" /> Delivered</Badge>
                      ) : d.delivery_status === "bounced" ? (
                        <Badge className="bg-red-100 text-red-700 gap-1"><XCircle className="w-3 h-3" /> Bounced</Badge>
                      ) : d.delivery_status === "spam" ? (
                        <Badge className="bg-orange-100 text-orange-700 gap-1"><AlertCircle className="w-3 h-3" /> Marked as spam</Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700 gap-1" title="Accepted by the email provider — awaiting delivery confirmation"><Clock className="w-3 h-3" /> Sent</Badge>
                      )}
                    </TableCell>
                    <TableCell className="mono text-xs">{d.provider || "—"}</TableCell>
                    <TableCell className="mono text-xs">{d.sent_at ? new Date(d.sent_at).toLocaleTimeString() : "—"}</TableCell>
                    <TableCell className="text-xs" style={{ color: "var(--text-soft)" }}>{d.bounce_reason || d.error || "—"}</TableCell>
                  </TableRow>
                ))}
                {!(c.deliveries || []).length && <TableRow><TableCell colSpan={6} className="text-center py-10" style={{ color: "var(--text-soft)" }}>No deliveries recorded yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="tracking" className="mt-4">
          {trackLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} /></div>
          ) : !tracking ? (
            <Card className="p-10 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <Activity className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-soft)" }} />
              <p style={{ color: "var(--text-soft)" }}>No tracking data yet. Send the campaign first.</p>
              <p className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>Tracking requires BACKEND_PUBLIC_URL to be set in your environment.</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Card className="p-4 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs mono uppercase font-semibold" style={{ color: "var(--text-soft)" }}>Total tracked</div>
                  <div className="display text-3xl mt-1" style={{ color: "var(--text)" }}>{tracking.total}</div>
                </Card>
                <Card className="p-4 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs mono uppercase font-semibold flex items-center justify-center gap-1" style={{ color: "var(--text-soft)" }}><MailOpen className="w-3 h-3" /> Opened</div>
                  <div className="display text-3xl mt-1 text-emerald-600">{tracking.opened}</div>
                  <div className="text-xs mono" style={{ color: "var(--text-soft)" }}>{tracking.total ? `${Math.round(tracking.opened/tracking.total*100)}%` : "0%"}</div>
                </Card>
                <Card className="p-4 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs mono uppercase font-semibold flex items-center justify-center gap-1" style={{ color: "var(--text-soft)" }}><MousePointerClick className="w-3 h-3" /> Clicked</div>
                  <div className="display text-3xl mt-1" style={{ color: "var(--brand)" }}>{tracking.clicked}</div>
                  <div className="text-xs mono" style={{ color: "var(--text-soft)" }}>{tracking.total ? `${Math.round(tracking.clicked/tracking.total*100)}%` : "0%"}</div>
                </Card>
              </div>
              <Card style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <Table>
                  <TableHeader><TableRow style={{ background: "var(--bg)" }}>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Clicked</TableHead>
                    <TableHead>Opened at</TableHead>
                    <TableHead>Clicked at</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(tracking.records || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold">{r.contact_name || "—"}</TableCell>
                        <TableCell className="text-sm mono" style={{ color: "var(--text-soft)" }}>{r.contact_email}</TableCell>
                        <TableCell>{r.opened ? <Badge className="bg-emerald-100 text-emerald-700 gap-1"><MailOpen className="w-3 h-3" /> Yes</Badge> : <span className="text-xs" style={{ color: "var(--text-soft)" }}>No</span>}</TableCell>
                        <TableCell>{r.clicked ? <Badge className="gap-1" style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)", color: "var(--brand)" }}><MousePointerClick className="w-3 h-3" /> Yes</Badge> : <span className="text-xs" style={{ color: "var(--text-soft)" }}>No</span>}</TableCell>
                        <TableCell className="mono text-xs" style={{ color: "var(--text-soft)" }}>{r.opened_at ? new Date(r.opened_at).toLocaleString() : "—"}</TableCell>
                        <TableCell className="mono text-xs" style={{ color: "var(--text-soft)" }}>{r.clicked_at ? new Date(r.clicked_at).toLocaleString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {!(tracking.records || []).length && <TableRow><TableCell colSpan={6} className="text-center py-10" style={{ color: "var(--text-soft)" }}>No tracking records</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="html" className="mt-4">
          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <pre className="mono text-xs p-4 rounded border overflow-x-auto whitespace-pre-wrap" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>{c.body}</pre>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
